/**
 * The console frame.
 *
 * ConsoleNav owns page navigation, AppShell owns the frame inside the content
 * area and the one detail drawer, and the router still owns where you are. A
 * deep link to one decision opens that decision and the back button still
 * means what it says (design package section 10).
 */

import { Route, Routes, useLocation } from 'react-router-dom'
import { makeStyles, tokens } from '@fluentui/react-components'
import {
  AppShell,
  EmptyState,
  LoadingState,
  type AppShellBanner,
  type AppShellDetailDrawerConfig,
} from './platform'
import { ConsoleNav } from './console/ConsoleNav'
import { SystemHealthIndicator } from './console/SystemHealth'
import { NAV_ENTRIES, navValueForPath } from './console/navigation'
import { SignIn } from './console/SignIn'
import { useAuth } from './auth'
import Communications, { communicationsDrawer } from './pages/Communications'
import Attempts from './pages/Attempts'
import ApprovalRatio from './pages/ApprovalRatio'
import Decisions, { decisionDrawer } from './pages/Decisions'
import DecisionDetail from './pages/DecisionDetail'
import Logs from './pages/Logs'
import OrphanSessions from './pages/OrphanSessions'
import Overview from './pages/Overview'
import RoutingPolicy from './pages/RoutingPolicy'
import Settings from './pages/Settings'
import WallEList from './pages/WallEList'
import DayByDay from './pages/DayByDay'

const DEV_BYPASS_BANNER: AppShellBanner = {
  intent: 'warning',
  title: 'Development sign-in',
  body:
    'You are signed in through the development bypass, not Microsoft. This ' +
    'environment has no real authentication, and it says so rather than ' +
    'looking authenticated.',
  dismissible: false,
}

/**
 * AppShell owns exactly one detail drawer, so the page in view decides what it
 * holds. A page with no drawer passes undefined and the shell renders none.
 */
const DRAWERS: Record<string, AppShellDetailDrawerConfig<unknown> | undefined> = {
  decisions: decisionDrawer as AppShellDetailDrawerConfig<unknown>,
  communications: communicationsDrawer as AppShellDetailDrawerConfig<unknown>,
}

/** For an address that is no page. Named rather than falling back to Overview,
 *  so the heading agrees with the body underneath it. */
const UNKNOWN_ADDRESS_HEADING = {
  title: 'Not found',
  subtitle: 'No page is served at this address',
}

// EXPORTED SO THE TWO LISTS CAN BE COMPARED. The navigation and this map are
// two configurations that must agree, kept in two places, with nothing checking
// them: adding a page to one and not the other renders the page under the
// heading "Not found", which is what happened the first time Attempts was
// opened in a browser. It is the shape of the residual orphan finding, one
// level down, and a test now fails rather than a reader being told a page they
// are looking at does not exist.
export const TITLES: Record<string, { title: string; subtitle: string }> = {
  overview: {
    title: 'Overview',
    subtitle: 'Is the service healthy, and is anything going unbilled',
  },
  'day-by-day': {
    title: 'Day by day',
    subtitle: 'Each day against the median of the same weekdays before it',
  },
  'orphan-sessions': {
    title: 'Orphan sessions',
    subtitle: 'Session events that named no stamp, and what the policy would have decided',
  },
  decisions: {
    title: 'Decisions',
    subtitle: 'Which token the station sent, who was asked, and what this service answered',
  },
  'approval-ratio': {
    title: 'Approval ratio',
    subtitle: 'What an authority answered against what this service answered',
  },
  attempts: {
    title: 'Attempts',
    subtitle: 'A card refused again and again, or one card at several charge points',
  },
  communications: {
    title: 'Communications',
    subtitle: 'Every message crossing the service, both faces',
  },
  'routing-policy': {
    title: 'Routing policy',
    subtitle: 'The rules actually in force, as deployed',
  },
  'wall-e-list': {
    title: 'Wall-E list',
    subtitle: 'The tokens this service approves or refuses itself, asking nobody',
  },
  logs: { title: 'Logs', subtitle: 'What the service actually said' },
  settings: { title: 'Settings', subtitle: 'Connections, mail and alerts' },
}

const useStyles = makeStyles({
  content: {
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground1,
  },
})

export default function App() {
  const styles = useStyles()
  const { principal, loading, error, usingDevBypass, signOut } = useAuth()
  const location = useLocation()

  if (loading) return <LoadingState label="Loading the console" />
  if (error) {
    return <EmptyState title="The service is not answering" description={error} />
  }
  if (!principal) return <SignIn />

  const current = navValueForPath(location.pathname)
  const heading = (current ? TITLES[current] : undefined) ?? UNKNOWN_ADDRESS_HEADING

  return (
    <ConsoleNav
      entries={NAV_ENTRIES}
      identity={principal}
      serviceName="EA Authorization"
      storageKey="walle.console.eas.nav.pinned"
      onSignOut={signOut}
      status={(placement) => <SystemHealthIndicator placement={placement} />}
    >
      <div className={styles.content}>
        <AppShell
          title={heading.title}
          subtitle={heading.subtitle}
          banner={usingDevBypass ? DEV_BYPASS_BANNER : undefined}
          detailDrawer={current ? DRAWERS[current] : undefined}
        >
          <ConsoleRoutes />
        </AppShell>
      </div>
    </ConsoleNav>
  )
}

function UnknownAddress() {
  const { pathname } = useLocation()
  return (
    <EmptyState
      title="There is no page at this address"
      description={`Nothing is served at ${pathname}. The pages are listed in the navigation; a link from somewhere older may have moved.`}
    />
  )
}

export function ConsoleRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Overview />} />
      <Route path="/day-by-day" element={<DayByDay />} />
      <Route path="/decisions" element={<Decisions />} />
      <Route path="/approval-ratio" element={<ApprovalRatio />} />
      <Route path="/attempts" element={<Attempts />} />
      <Route path="/decisions/:id" element={<DecisionDetail />} />
      <Route path="/orphan-sessions" element={<OrphanSessions />} />
      <Route path="/routing-policy" element={<RoutingPolicy />} />
      <Route path="/wall-e-list" element={<WallEList />} />
      <Route path="/communications" element={<Communications />} />
      <Route path="/logs" element={<Logs />} />
      <Route path="/settings" element={<Settings />} />
      {/* An address that matches nothing renders the shell and nothing else,
          which reads as a broken console rather than a wrong address. This
          console exists so an operator can find out why something happened,
          so it says which address it was. */}
      <Route path="*" element={<UnknownAddress />} />
    </Routes>
  )
}
